import type { CSSProperties } from 'react';
import { COLUMNS_COLORS, type ColorToken } from '../lib/colors';
import { COLUMNS_SPACING_PX, type SpacingToken } from '../lib/spacing';
import { StructuredContentRenderer } from '../renderer';
import type { NodeProps, StructuredContent } from '../types';

const BORDER_RADIUS = ['0', '0.125rem', '0.25rem'] as const;

type ColumnWidth = number | string;
type ColumnStyle = CSSProperties & Record<`--sc-${string}`, string | undefined>;

export type ColumnProps = NodeProps<{
  __collapse?: boolean;
  backgroundColor?: ColorToken;
  borderRadius?: 0 | 1 | 2 | '0' | '1' | '2';
  children: StructuredContent | StructuredContent[];
  columnStart?: ColumnWidth;
  equalHeight?: boolean;
  order?: { desktop?: number; mobile?: number };
  padding?: SpacingToken;
  width?: ColumnWidth | { desktop: ColumnWidth; mobile: ColumnWidth };
}>;

export function Column({ props }: ColumnProps) {
  const width = props.width;
  const mobileWidth =
    typeof width === 'object' && width?.desktop !== undefined ? width.mobile : width;
  const desktopWidth =
    typeof width === 'object' && width?.desktop !== undefined ? width.desktop : width;
  const orderMobile = props.order?.mobile;
  const orderDesktop = props.order?.desktop;
  const style: ColumnStyle = {
    '--sc-column-start': props.__collapse ? 'unset' : String(props.columnStart ?? ''),
    '--sc-column-end-mobile': props.__collapse ? 'unset' : `span ${Number(mobileWidth)}`,
    '--sc-column-end-desktop': `span ${Number(desktopWidth)}`,
    '--sc-order-mobile': orderMobile != null ? String(orderMobile) : undefined,
    '--sc-order-desktop': orderDesktop != null ? String(orderDesktop) : undefined,
    height: props.equalHeight ? '100%' : undefined,
    padding: COLUMNS_SPACING_PX[props.padding ?? 0],
    borderRadius: BORDER_RADIUS[Number(props.borderRadius ?? 0)],
    backgroundColor: COLUMNS_COLORS[props.backgroundColor ?? 'white'],
  };

  return (
    <div
      data-slot="sc-column"
      className={`!col-start-[var(--sc-column-start)] col-end-[var(--sc-column-end-mobile)] sm:col-end-[var(--sc-column-end-desktop)]${orderMobile != null ? ' order-[var(--sc-order-mobile)]' : ''}${orderDesktop != null ? ' sm:order-[var(--sc-order-desktop)]' : ''}`}
      style={style}
    >
      <StructuredContentRenderer doc={props.children} />
    </div>
  );
}
